import { useHRDashboard } from '../hooks/useHCM';
import { Card, CardContent, CardHeader, CardTitle } from '@travellegacy/nexops-design-system';
import { Spinner } from '@travellegacy/nexops-design-system';
import { Users, DollarSign, Cake, Award, Building2 } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

export function HRDashboardPage() {
  const { data: dashboard, isLoading } = useHRDashboard();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!dashboard) {
    return <div className="p-8 text-center">Erro ao carregar dashboard</div>;
  }

  const departmentChartData = dashboard.employeesByDepartment.map((dept) => ({
    name: dept.departmentName,
    value: dept.count,
  }));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Dashboard de RH</h1>
        <p className="text-muted-foreground">Métricas e indicadores de Capital Humano</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Colaboradores</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboard.totalEmployees}</div>
            <p className="text-xs text-muted-foreground">
              {dashboard.activeEmployees} ativos
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Custo Total da Folha</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              R$ {dashboard.totalPayrollCost.total.toLocaleString('pt-BR')}
            </div>
            <p className="text-xs text-muted-foreground">
              {dashboard.totalPayrollCost.employeeCount} colaboradores
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Aniversariantes do Mês</CardTitle>
            <Cake className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboard.birthdays.length}</div>
            <p className="text-xs text-muted-foreground">
              {dashboard.birthdays.length > 0
                ? `Próximo: ${dashboard.birthdays[0]?.name}`
                : 'Nenhum aniversário'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Aniversários de Empresa</CardTitle>
            <Award className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboard.workAnniversaries.length}</div>
            <p className="text-xs text-muted-foreground">
              {dashboard.workAnniversaries.length > 0
                ? `${dashboard.workAnniversaries[0]?.yearsOfService} anos - ${dashboard.workAnniversaries[0]?.name}`
                : 'Nenhum aniversário'}
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Colaboradores por Departamento
            </CardTitle>
          </CardHeader>
          <CardContent>
            {departmentChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={departmentChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {departmentChartData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-muted-foreground py-8">Nenhum dado disponível</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Cake className="h-5 w-5" />
              Próximos Aniversários (30 dias)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {dashboard.upcomingBirthdays.length === 0 ? (
                <p className="text-center text-muted-foreground py-4">Nenhum aniversário nos próximos 30 dias</p>
              ) : (
                dashboard.upcomingBirthdays.map((birthday) => {
                  // Usar UTC para evitar problemas de timezone
                  const date = new Date(birthday.birthDate);
                  const day = date.getUTCDate();
                  const month = date.toLocaleDateString('pt-BR', {
                    month: 'long',
                    timeZone: 'UTC',
                  });

                  return (
                    <div key={birthday.id} className="flex justify-between items-center border-b pb-2">
                      <div>
                        <p className="font-semibold">{birthday.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {day} de {month}
                        </p>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {birthday.daysUntilBirthday === 0
                          ? 'Hoje!'
                          : birthday.daysUntilBirthday === 1
                          ? 'Amanhã'
                          : `Em ${birthday.daysUntilBirthday} dias`}
                      </p>
                    </div>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="h-5 w-5" />
              Aniversários de Empresa
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {dashboard.workAnniversaries.length === 0 ? (
                <p className="text-center text-muted-foreground py-4">Nenhum aniversário de empresa este mês</p>
              ) : (
                dashboard.workAnniversaries.map((anniversary) => {
                  // Usar UTC para evitar problemas de timezone
                  const date = new Date(anniversary.admissionDate);
                  const formattedDate = date.toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    timeZone: 'UTC',
                  });

                  return (
                    <div key={anniversary.id} className="flex justify-between items-center border-b pb-2">
                      <div>
                        <p className="font-semibold">{anniversary.name}</p>
                        <p className="text-sm text-muted-foreground">
                          Admitido em {formattedDate}
                        </p>
                      </div>
                      <p className="text-sm font-semibold text-primary">
                        {anniversary.yearsOfService} {anniversary.yearsOfService === 1 ? 'ano' : 'anos'}
                      </p>
                    </div>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
